import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useSettingsStore } from '@/stores/settings'

const routes = [
  {
    path: '/',
    name: 'home',
    component: () => import('@/views/HomeView.vue'),
  },
  {
    path: '/callback',
    name: 'callback',
    component: () => import('@/views/CallbackView.vue'),
  },
  {
    // Onboarding for users with zero company memberships. Exempt from the
    // requiresCompany guard (it IS the company-selection page).
    path: '/company-selection',
    name: 'company-selection',
    component: () => import('@/views/CompanySelectionView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/facturacion',
    name: 'facturacion',
    component: () => import('@/views/BillingView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/',
    component: () => import('@/layouts/DashboardLayout.vue'),
    // requiresCompany: every data view needs an active company. The
    // /settings route (company management) is exempt via its own
    // meta override below.
    meta: { requiresAuth: true, requiresCompany: true },
    children: [
      {
        path: 'dashboard',
        name: 'dashboard',
        component: () => import('@/views/DashboardView.vue'),
      },
      {
        path: 'inbox',
        name: 'inbox',
        component: () => import('@/views/InboxView.vue'),
      },
      // v2 architecture (Domain 1: Recepción) — DIAN portal importer
      // (Playwright worker). Standalone view that posts to
      // /dian-importer/import and listens to dian_import.* WS events.
      {
        path: 'inbox/import-from-dian/:id?',
        name: 'dian-import',
        component: () => import('@/views/DianImportView.vue'),
      },
      {
        path: 'invoices',
        component: () => import('@/views/InvoicesView.vue'),
        children: [
          {
            path: '',
            name: 'invoices',
            component: () => import('@/views/InvoiceElectronicList.vue'),
          },
          {
            path: ':id',
            name: 'invoice-detail',
            component: () => import('@/views/InvoiceDetailView.vue'),
          }
        ]
      },
      {
        path: 'invoices/:id/review',
        name: 'invoice-review',
        component: () => import('@/views/ReviewWorkspaceView.vue'),
      },
      // v1.9 Phase 32 Plan 32-05: Pre-Acusé queue (invoice
      // acknowledgment state machine). Lists invoices in `pre_acuse`
      // for the current company with multi-select + batch advance.
      {
        path: 'pre-acuse',
        name: 'pre-acuse',
        component: () => import('@/views/PreAcuseView.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: 'pre-caused',
        name: 'pre-caused',
        component: () => import('@/views/PreCausedView.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: 'integrations',
        name: 'integrations',
        component: () => import('@/views/IntegrationsView.vue'),
      },
      {
        path: 'copilot',
        name: 'copilot',
        component: () => import('@/views/CopilotView.vue'),
      },
      {
        path: 'queries/:id?',
        name: 'queries',
        component: () => import('@/views/InvoiceQueriesView.vue'),
      },
      {
        path: 'suppliers',
        name: 'suppliers',
        component: () => import('@/views/SuppliersView.vue'),
      },
      // DocumentsView existía y hasta se precargaba, pero nunca se
      // enrutó: era inalcanzable desde la navegación. Es el repositorio
      // de ARCHIVOS cargados, distinto de /invoices (que el menú rotula
      // "Documentos" y lista facturas ya vinculadas).
      {
        path: 'documents',
        name: 'documents',
        component: () => import('@/views/DocumentsView.vue'),
      },
      {
        path: 'settings',
        name: 'settings',
        component: () => import('@/views/SettingsView.vue'),
        // Exempt: the user needs to reach Settings to create/select a company.
        meta: { requiresCompany: false },
      },
      // v1.8 Phase 28 Plan 28-02 (FISCAL-01, D-15): admin-only
      // `super_admin` route for the fiscal-tables editor. The
      // beforeEach guard below also enforces `meta.requiresRole` so
      // non-super_admin users are redirected to /dashboard.
      {
        path: 'admin/fiscal-tables',
        name: 'admin-fiscal-tables',
        component: () => import('@/views/admin/fiscal-tables.vue'),
        meta: { requiresAuth: true, requiresRole: 'super_admin' },
      },
    ],
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
})

// Prefetch en idle de los chunks de las vistas principales. Cada ruta
// es un dynamic import → sin esto, el primer click a cada página paga
// el fetch+parse del chunk (perceptible como ~0.5-1s de demora una vez
// por vista). Tras la primera navegación exitosa, descargamos el resto
// en background cuando el main thread está libre.
let viewsPrefetched = false
function prefetchViewsOnIdle() {
  if (viewsPrefetched) return
  viewsPrefetched = true
  const run = () => {
    void Promise.allSettled([
      import('@/views/DashboardView.vue'),
      import('@/views/InboxView.vue'),
      import('@/views/DianImportView.vue'),
      import('@/views/DocumentsView.vue'),
      import('@/views/PreAcuseView.vue'),
      import('@/views/PreCausedView.vue'),
      import('@/views/SuppliersView.vue'),
      import('@/views/InvoiceQueriesView.vue'),
      import('@/views/IntegrationsView.vue'),
      import('@/views/CopilotView.vue'),
      import('@/views/SettingsView.vue'),
    ])
  }
  if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
    window.requestIdleCallback(run, { timeout: 3000 })
  } else {
    setTimeout(run, 1500)
  }
}

router.afterEach((to) => {
  // Solo tras entrar al área autenticada — no adelantamos nada en la
  // pantalla de login.
  if (to.meta.requiresAuth) prefetchViewsOnIdle()
})

router.beforeEach(async (to) => {
  if (!to.meta.requiresAuth) return true

  const auth = useAuthStore()
  await auth.bootstrap()
  if (!auth.isAuthenticated) {
    return { name: 'home', query: { redirect: to.fullPath } }
  }

  // Company requirement: data-scoped routes need an active company.
  //
  // `force: false` opts in to the settings-store TTL cache (default
  // 60s) — without this, every click on a sidebar item, document
  // tab, or invoices-table row blocked here on two sequential HTTP
  // calls (GET /companies + GET /companies/current) for a permanent
  // ~1s click-to-paint delay across the whole app. The cache is
  // invalidated explicitly by every mutation site
  // (createCompany/uploadLogo/uploadRut/createCompany etc. all
  // default to `force: true`).
  if (to.meta.requiresCompany) {
    const settings = useSettingsStore()
    const hasUsableCompany = () =>
      Boolean(settings.activeCompanyId) &&
      settings.availableCompanies.some((c) => c.id === settings.activeCompanyId)

    // Stale-while-revalidate: si ya hay una empresa activa válida en
    // memoria, la navegación NO espera el refresh — se dispara en
    // background y el TTL del store deduplica. Sin esto, cada
    // expiración del TTL (60s) convertía el siguiente click en ~1s de
    // espera por dos HTTP secuenciales antes de resolver la ruta.
    // Solo el primer arranque (store vacío) bloquea, porque ahí sí no
    // hay dato con el cual decidir el redirect a onboarding.
    if (hasUsableCompany()) {
      void settings.refreshCompany({ force: false })
    } else {
      await settings.refreshCompany({ force: false })
    }
    if (!hasUsableCompany()) {
      // No usable company → send to onboarding (create first company).
      return { name: 'company-selection' }
    }
  }

  // v1.8 Phase 28 Plan 28-02 (D-15): role-gate check. Routes that
  // declare `meta.requiresRole` (e.g. `super_admin`) are restricted
  // to users with that role in their `roles` claim. Non-matching
  // users are redirected to /dashboard. We check `auth.user.role`
  // (the primary role) AND `auth.user.roles` (the array claim) —
  // whichever is populated.
  const requiredRole = to.meta.requiresRole as string | undefined
  if (requiredRole) {
    const userRoles = auth.user?.roles ?? []
    const primaryRole = auth.user?.role
    const hasRole =
      userRoles.includes(requiredRole) || primaryRole === requiredRole
    if (!hasRole) {
      return { name: 'dashboard', query: { denied: to.fullPath } }
    }
  }

  return true
})
